import {
  IPaymentGatewayAccountSchema,
  IPaymentMethodResp,
} from '@monetize/types/app';
import { FunctionComponent as FC, useEffect, useRef, useState } from 'react';
import { handleApiErrorToast } from '../../../api/axios';
import { useSetupPaymentGatewayForPayment } from '../../../api/paymentsService';
import useCurrentPaymentGateways from '../../../hooks/useCurrentPaymentGateways';
import { useAuth } from '../../../services/auth0';
import { MCenterModal } from '../../Monetize';
import { StripePaymentMethodFormFields } from './StripePaymentMethodFormFields';
import { StripeWrapper } from './StripeWrapper';

interface StripePaymentMethodFormProps {
  isOpen: boolean;
  onClose: (paymentMethod?: IPaymentMethodResp) => void;
  accountId: string;
}

export const NewStripePaymentMethodModal: FC<StripePaymentMethodFormProps> = (
  props: StripePaymentMethodFormProps,
) => {
  const { isOpen, onClose, accountId } = props;
  const { tenantId } = useAuth();
  /** sed to avoid duplicate requests */
  const hasMadeRequest = useRef(false);
  const { activeGateways: paymentGateway, currentPaymentGateway } =
    useCurrentPaymentGateways({
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    });

  const [gatewayAccount, setGatewayAccount] =
    useState<IPaymentGatewayAccountSchema | null>(null);
  const [setupIntent, setSetupIntent] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  const { isPending: isLoading, mutate: initPaymentData } =
    useSetupPaymentGatewayForPayment({
      onSuccess: (response) => {
        setGatewayAccount(response.gatewayAccount);
        setSetupIntent(response.setupIntent.setupIntentId);
        setClientSecret(response.clientSecret.clientSecret);
      },
      onError: (error) => {
        onClose();
        handleApiErrorToast(error);
      },
      onSettled: () => {
        hasMadeRequest.current = false;
      },
    });

  useEffect(() => {
    if (
      !hasMadeRequest.current &&
      isOpen &&
      accountId &&
      tenantId &&
      currentPaymentGateway
    ) {
      hasMadeRequest.current = true;
      initPaymentData({
        accountId,
        paymentGateway: currentPaymentGateway,
      });
    }
  }, [accountId, isOpen, initPaymentData, tenantId, currentPaymentGateway]);

  if (isLoading) {
    // The modal's own spinner, not the full-page AppLoading: this flow renders
    // inside other modals (Pay Invoice), where AppLoading's 100vw x 100vh box
    // was anchored to that modal's corner instead of covering the page.
    return (
      <MCenterModal
        isOpen={isOpen}
        onClose={onClose}
        modalTitle="New Payment Method"
        size="lg"
        closeOnEsc={false}
        closeOnOverlayClick={false}
        isLoading
      />
    );
  }

  if (!paymentGateway || !gatewayAccount || !setupIntent || !clientSecret) {
    return null;
  }

  return (
    <StripeWrapper
      apiKey={currentPaymentGateway?.publicKey!}
      clientSecret={clientSecret || ''}
    >
      <StripePaymentMethodFormFields
        {...props}
        accountId={accountId}
        paymentGateway={currentPaymentGateway!}
        setupIntent={setupIntent || ''}
        onClose={onClose}
      />
    </StripeWrapper>
  );
};
